import type { WalletKind } from '../model/types';

export const WALLET_KIND_LABEL_KEYS: Record<WalletKind, string> = {
  working: 'serviceAccounting.wallets.working',
  deposit: 'serviceAccounting.wallets.deposit',
  payout: 'serviceAccounting.wallets.payout',
  service: 'serviceAccounting.wallets.service',
};

const WALLET_KINDS = new Set<string>(['working', 'deposit', 'payout', 'service']);

/** Maps API wallet kind (lowercase from service accounting, uppercase from /wallets). */
export const parseWalletKind = (kind?: string | null): WalletKind => {
  const normalized = kind?.toLowerCase();
  if (normalized && WALLET_KINDS.has(normalized)) {
    return normalized as WalletKind;
  }

  return 'service';
};

export const isServiceAccountingWallet = (kind?: string | null): boolean => {
  const normalized = kind?.toLowerCase();
  return Boolean(normalized && WALLET_KINDS.has(normalized));
};

export type AccountingOperationWalletRef = {
  kind?: string | null;
  group?: { code?: string | null } | null;
};

/** Wallets allowed in POST /balances/accountings (service + merchants group + merchant ledger). */
export const isWalletSelectableForAccountingOperation = (
  wallet: AccountingOperationWalletRef,
): boolean => {
  const groupCode = wallet.group?.code;
  if (groupCode === 'partners') return false;
  if (groupCode === 'merchants') return true;
  if (isServiceAccountingWallet(wallet.kind)) return true;
  if (!wallet.kind && groupCode === 'project_virtual') return true;
  return false;
};

/** Wallets that were shown before merchant / `other` kinds were added to the operation picker. */
export const isLegacyOperationWallet = (wallet: AccountingOperationWalletRef): boolean =>
  isServiceAccountingWallet(wallet.kind);

/** Service wallets first (API order), then the rest of selectable wallets (API order). */
export const sortWalletsForAccountingOperation = <T extends AccountingOperationWalletRef>(
  wallets: T[],
): T[] => {
  const primary: T[] = [];
  const secondary: T[] = [];

  for (const wallet of wallets) {
    if (!isWalletSelectableForAccountingOperation(wallet)) continue;
    if (isLegacyOperationWallet(wallet)) primary.push(wallet);
    else secondary.push(wallet);
  }

  return [...primary, ...secondary];
};
